import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import GoalCard from './GoalCard';
import { Goal } from '../store/useFinanceStore';
import { useTheme } from '../contexts/ThemeContext';

interface GoalsCarouselProps {
  goals: Goal[];
  onNavStateChange?: (state: { canGoPrev: boolean; canGoNext: boolean }) => void;
}

export interface GoalsCarouselHandle {
  goToPrev: () => void;
  goToNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
}

const GoalsCarousel = forwardRef<GoalsCarouselHandle, GoalsCarouselProps>(({ goals, onNavStateChange }, ref) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemsPerView, setItemsPerView] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerView(2);
      } else if (window.innerWidth >= 640) {
        setItemsPerView(1);
      } else {
        setItemsPerView(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, goals.length - itemsPerView);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  }, [maxIndex]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      setCurrentIndex(Math.min(index, maxIndex));
    },
    [maxIndex]
  );

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const pageX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    setStartX(pageX);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const pageX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    const diff = pageX - startX;
    setTranslateX(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (translateX > 100 && currentIndex > 0) {
      goToPrev();
    } else if (translateX < -100 && currentIndex < maxIndex) {
      goToNext();
    }
    setTranslateX(0);
  };

  const totalDots = Math.ceil(goals.length / itemsPerView);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;

  useImperativeHandle(
    ref,
    () => ({
      goToPrev,
      goToNext,
      canGoPrev,
      canGoNext,
    }),
    [goToNext, goToPrev, canGoNext, canGoPrev]
  );

  useEffect(() => {
    onNavStateChange?.({ canGoPrev, canGoNext });
  }, [canGoNext, canGoPrev, onNavStateChange]);

  if (goals.length === 0) {
    return (
      <div
        className={`rounded-2xl border p-8 text-center transition-colors ${
          isDark
            ? 'border-slate-700/50 bg-slate-900/70 text-slate-300'
            : 'border-teal-200 bg-white text-slate-600 shadow-sm'
        }`}
      >
        Nenhuma meta cadastrada. Crie uma nova meta para começar a acompanhar seu progresso.
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      <div
        ref={containerRef}
        className="overflow-hidden px-2 sm:px-4"
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div
          className="flex gap-6 transition-transform duration-500 ease-out"
          style={{
            transform: `translateX(calc(-${currentIndex * (100 / itemsPerView)}% - ${currentIndex * 1.5}rem + ${translateX}px))`,
          }}
        >
          {goals.map((goal) => (
            <div
              key={goal.id}
              className={`flex-shrink-0 ${
                itemsPerView === 1 ? 'w-full' : 'w-[calc(50%-0.75rem)]'
              }`}
            >
              <GoalCard goal={goal} />
            </div>
          ))}
        </div>
      </div>

      {totalDots > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          {Array.from({ length: totalDots }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index * itemsPerView)}
              className={`h-2 rounded-full transition-all duration-300 ${
                Math.floor(currentIndex / itemsPerView) === index
                  ? isDark
                    ? 'w-8 bg-gradient-to-r from-cyan-400 to-blue-500'
                    : 'w-8 bg-gradient-to-r from-teal-500 to-emerald-500'
                  : isDark
                    ? 'w-2 bg-slate-700 hover:bg-slate-600'
                    : 'w-2 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`Ir para página ${index + 1} de metas`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default GoalsCarousel;
